import React, { useState } from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import type { ReportTheme } from '../../../../types/reportTheme';
import { SectionShell } from '../SectionShell';
import { walkClaimText } from '../../../../lib/walkClaimText';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor, roleColorAlpha } from '../../../../lib/reportRoleColors';
import { EditorialBadge, EditorialSubsection, type EditorialBadgeTone } from '../../report/editorial';
import { useViewMode, isExportMode } from '../../viewMode';
import { REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

const MONO = 'var(--report-body-font, inherit)';

/** Viability tier → EditorialBadge tone (replaces filled pill backgrounds). */
const VIABILITY_TONE: Record<string, EditorialBadgeTone> = {
  primary: 'positive',
  alternative: 'accent',
  disqualified: 'negative',
  'not-applicable': 'neutral',
};

const VIABILITY_LABELS: Record<string, string> = {
  primary: 'Primary',
  alternative: 'Alternative',
  disqualified: 'Disqualified',
  'not-applicable': 'N/A',
};

function slugifyTech(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export const BtmTechComparisonSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const rows = report.btmDetailedAnalysis ?? [];
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  // P3 FORCE-MOUNT — the per-tech detail body is conditionally MOUNTED below
  // (`{isOpen && (...)}`), so a 'disqualified'/'not-applicable' tech that the
  // on-screen viewer collapses is ABSENT (not merely hidden) in the PDF/DOCX.
  // When rendering for the customer (export viewMode) force EVERY card open so
  // the full comparison reaches the deliverable; the interactive collapse is
  // preserved on-screen. Both render trees wrap the export path in
  // <ViewModeProvider value="export">, so this is a pure consumer read.
  const exporting = isExportMode(useViewMode());

  const toggle = (i: number) => setExpanded(prev => ({ ...prev, [i]: !prev[i] }));

  return (
    <SectionShell label={label} hasData={rows.length > 0} report={report}>
      <div className="space-y-4">
        {rows.map((btm, i) => {
          const tier = btm.viabilityTier ?? 'alternative';
          const tierTone = VIABILITY_TONE[tier] ?? VIABILITY_TONE.alternative;
          const tierLabel = VIABILITY_LABELS[tier] ?? tier;
          const isOpen = exporting ? true : (expanded[i] ?? (tier === 'primary' || tier === 'alternative'));
          const techSlug = slugifyTech(btm.technology);

          return (
            <div
              key={`btm-${i}`}
              id={`btm-tech-${techSlug}`}
              className="overflow-hidden"
              style={{ borderTop: `1px solid ${roleColor(theme, 'rule')}` }}
              data-testid={`btm-card-${techSlug}`}
            >
              <button
                type="button"
                className="w-full flex items-center justify-between gap-3 py-3 text-left transition-colors"
                onClick={() => toggle(i)}
                aria-expanded={isOpen}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-bold truncate" style={{ color: roleColor(theme, 'ink') }}>
                    {btm.technology}
                  </span>
                  <EditorialBadge theme={theme} tone={tierTone} variant="outline">
                    {tierLabel}
                  </EditorialBadge>
                </div>
                <span
                  className="text-xs shrink-0"
                  style={{ fontFamily: MONO, color: roleColor(theme, 'muted') }}
                >
                  {btm.lcoeRange}
                </span>
              </button>

              {isOpen && (
                <div className="pb-4 pt-1 space-y-3">
                  {btm.intentAlignment?.trim() && (
                    <p
                      className="text-xs italic m-0"
                      // William QA 2026-06-28 — left accent rule removed report-wide
                      style={{
                        color: roleColor(theme, 'muted'),
                      }}
                    >
                      {walkClaimText(btm.intentAlignment, report.references)}
                    </p>
                  )}

                  <div className={`grid grid-cols-2 ${theme.bodyColumns === 2 ? '' : 'lg:grid-cols-3'} gap-3 text-xs`}>
                    <Field theme={theme} label="Capital" value={btm.capitalCost} />
                    <Field theme={theme} label="Time-to-power" value={btm.timelineToFirstPower} />
                    <Field theme={theme} label="Land" value={btm.landRequirement} />
                    <Field theme={theme} label="Carbon" value={btm.carbonIntensity} />
                    <Field theme={theme} label="Operating model" value={btm.operatingModel} />
                    <Field theme={theme} label="Financial structure" value={btm.financialStructure} />
                  </div>

                  {btm.regulatoryPathway?.trim() && (
                    <EditorialSubsection theme={theme} label="Regulatory pathway">
                      {walkClaimText(btm.regulatoryPathway, report.references)}
                    </EditorialSubsection>
                  )}
                  {btm.siteSpecificFactors?.trim() && (
                    <EditorialSubsection theme={theme} label="Site-specific factors" accentLabel>
                      {walkClaimText(btm.siteSpecificFactors, report.references)}
                    </EditorialSubsection>
                  )}
                  {btm.recommendation?.trim() && (
                    <EditorialSubsection theme={theme} label="Recommendation">
                      {walkClaimText(btm.recommendation, report.references)}
                    </EditorialSubsection>
                  )}

                  {(report.lcoeDetailedAnalysis?.length ?? 0) > 0 && (
                    <p className="text-[10px] m-0" style={{ color: roleColor(theme, 'faint') }}>
                      LCOE detail: see{' '}
                      <a
                        href="#lcoeBreakdown"
                        className="underline underline-offset-2"
                        style={{ color: roleColor(theme, 'accent') }}
                      >
                        LCOE Breakdown
                      </a>{' '}
                      for {btm.technology}.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </SectionShell>
  );
};

/**
 * Field — a single BTM definition pair. Sizing/spacing track
 * sectionBlockChrome(theme)'s defLabelStyle / defBodyStyle by headingScale
 * so each preset reads consistently (no hardcoded fontSize:10 / mt-0.5).
 */
const Field: React.FC<{ theme: ReportTheme; label: string; value: string }> = ({ theme, label, value }) => {
  const scale = theme.headingScale;
  const defLabelStyle: React.CSSProperties =
    scale === 'technical'
      ? { fontFamily: MONO, fontSize: REPORT_TEXT_PX.caption, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: roleColor(theme, 'muted'), marginBottom: 3 }
      : scale === 'compact'
        ? { fontFamily: MONO, fontSize: REPORT_TEXT_PX.micro, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: roleColor(theme, 'muted'), marginBottom: 1 }
        : { fontFamily: MONO, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: roleColor(theme, 'muted'), marginBottom: 4 };
  const defBodyStyle: React.CSSProperties =
    scale === 'technical'
      ? { fontSize: 13, lineHeight: 1.55, color: roleColor(theme, 'ink') }
      : scale === 'compact'
        ? { fontSize: REPORT_TEXT_PX.label, lineHeight: 1.45, color: roleColor(theme, 'ink') }
        : { fontSize: 13.5, lineHeight: 1.6, color: roleColor(theme, 'ink') };
  return (
    <div>
      <span style={defLabelStyle}>{label}</span>
      <div style={defBodyStyle}>{value}</div>
    </div>
  );
};
